import { getServerApplicationContext } from '@hc-frontend/core-context';
import type {
  Applicant,
  Child,
  FeaturedAd,
  Spouse,
} from '@hc-frontend/core-types';
import { AdServer, QualifyingLifeEvent } from '@hc-frontend/core-types';
import { fetchBasedCall } from '@hc-frontend/core-utils-net';
import { randomUUID } from 'crypto';

export type QLE =
  | 'none'
  | 'marriage or divorce'
  | 'job loss / job change'
  | 'permanent move'
  | 'birth or adoption of a child'
  | 'other';

type Gender = 'M' | 'F';
type Tobacco = 0 | 1;
export type Member = {
  name?: string;
  gender: Gender;
  birth_date: string;
  tobacco: Tobacco;
};

export function mapQLE(qle?: QualifyingLifeEvent): QLE {
  switch (qle) {
    case QualifyingLifeEvent.Enum.None:
      return 'none';

    case QualifyingLifeEvent.Enum.Marriage:
    case QualifyingLifeEvent.Enum.Divorce:
      return 'marriage or divorce';

    case QualifyingLifeEvent.Enum.Baby:
      return 'birth or adoption of a child';

    case QualifyingLifeEvent.Enum.Moved:
      return 'permanent move';

    case QualifyingLifeEvent.Enum.IncomeChanged:
      return 'job loss / job change';

    default:
      return 'other';
  }
}

export function getMember(
  member?: Child | Applicant | Spouse,
): Member | undefined {
  if (member && member.dateOfBirth && member.gender) {
    return {
      name: member.firstName,
      gender: member.gender === 'MALE' ? 'M' : 'F',
      birth_date: member.dateOfBirth.toISOString().split('T')[0],
      tobacco: member.tobaccoUse === 'YES' ? 1 : 0,
    };
  }

  return undefined;
}

type Body = {
  api_token: string;
  placement_id: string;
  version: number;
  ip: string;
  ua: string;
  ua_language: string;
  url: string;
  sub_1?: string;
  sub_2?: string;
  sub_3?: string;
  cms_smids?: string | Array<string>;
  data: {
    zip: string;
    county: string;
    contact?: string;
    email?: string;
    phone?: string;
    address?: string;
    primary?: Member;
    spouse?: Member;
    children?: Array<Member | undefined>;
    household_income?: number;
    household_size?: number;
    qualifying_life_event?: QLE;
    gender?: Gender;
    birth_date?: string;
    tobacco?: Tobacco;
  };
};

function addProtocol(url: string): string {
  return (!/^https?:/i.test(url) ? 'https:' : '') + url;
}

/**
 * Requests ads from MediaAlpha and maps them to `FeaturedAd`.
 * Returns an empty list on any failure, including when `timeoutMs` elapses.
 */
export async function fetchMediaAlphaAdsInternal(
  url: string,
  body: Body,
  { timeoutMs }: { timeoutMs?: number } = {},
): Promise<Array<FeaturedAd>> {
  try {
    const responseFetch = await fetchBasedCall<
      typeof body,
      {
        num_ads: number;
        time: number;
        request_id: string;
        ads: Array<{
          bid: number;
          ad_id: string;
          medium_image_url: string;
          headline: string;
          description: string;
          carrier: string;
          display_url: string;
          click_url: string;
        }>;
      }
    >({
      url,
      method: 'POST',
      body,
      ...(timeoutMs ? { signal: AbortSignal.timeout(timeoutMs) } : {}),
    });

    if (
      responseFetch.result === 'success' &&
      Array.isArray(responseFetch.body?.ads) &&
      responseFetch.body.ads.length > 0
    ) {
      return responseFetch.body.ads.map((ad) => ({
        adsSource: AdServer.enum.MediaAlpha,
        id: randomUUID(),
        logo: addProtocol(ad.medium_image_url),
        title: ad.headline,
        content: ad.description,
        provider: {
          id: undefined,
          name: ad.carrier,
          displayUrl: ad.display_url,
        },
        link: addProtocol(ad.click_url),
        creativeId: parseInt(ad.ad_id, 10),
        groupId: undefined,
        campaignId: undefined,
        campaignName: undefined,
        dealType: 'CPC',
        cpc: ad.bid,
      }));
    }
  } catch (error) {
    const context = getServerApplicationContext(fetchMediaAlphaAdsInternal);
    const timedOut = error instanceof Error && error.name === 'TimeoutError';

    context.log({
      type: 'error',
      context: 'server',
      message: timedOut ? 'mediaAlphaAdsTimeout' : 'failedToGetMediaAlphaAds',
      data: {
        rootCause: new Error(
          timedOut
            ? `Media alpha ads timed out after ${timeoutMs}ms`
            : 'Failed to fetch media alpha ads',
        ),
      },
    });
  }

  return [];
}
